import { pack } from '@attain-apps/data-utils'
import { Multirange, Range } from '@attain-apps/range'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as types from './types'

// NOTE: The Excel date serial epoch is December 30, 1899 (UTC). SheetJS uses
// this specific base — rather than January 1, 1900 — to silently absorb the
// Lotus 1-2-3 "phantom Feb 29 1900" bug that Excel inherited: anchoring one
// day earlier means every serial above 60 maps to the correct calendar date
// with no special-case branching.
const _EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)
// NOTE: Excel's maximum valid date serial (December 31, 2099 = 2,958,465).
// Used as an upper bound to distinguish Excel serials from Unix-epoch
// millisecond timestamps, which for any plausible calendar date are orders
// of magnitude larger (e.g. ~1.7 × 10¹² for year 2025).
const _EXCEL_MAX_SERIAL = 2_958_466

/**
 * The raw value that a {@link Value} of this {@link Type} expects
 */
export type Raw<TType extends types.Type = types.Type> = {
  [types.Intrinsic.Boolean]: boolean
  [types.Intrinsic.Currency]: number
  [types.Intrinsic.Date]: Date
  [types.Intrinsic.DateMultirange]: Multirange<Date>
  [types.Intrinsic.DateRange]: Range<Date>
  [types.Intrinsic.Decimal]: number
  [types.Intrinsic.Integer]: number
  [types.Intrinsic.List]: readonly unknown[]
  [types.Intrinsic.Object]: Record<string, unknown>
  [types.Intrinsic.Percent]: number
  [types.Intrinsic.Text]: string
  [types.Intrinsic.Url]: string
}[Extract<types.Intrinsic.From<TType>, types.Intrinsic>]
export namespace Raw {
  export const isRaw = TypeChecker.Factory.custom(
    (val: any): val is Raw => {
      // If we can infer a valid type, then it should be a valid raw value
      try {
        return types.Intrinsic.isIntrinsic(types.Intrinsic.inferFrom(val))
      } catch (err) {
        return false
      }
    },
    { typeDescription: 'common.value.Raw' },
  )
  export const assertIsRaw: TypeAsserter<Raw> = TypeAsserter.Factory.from(isRaw)

  /**
   * Returns a {@link TypeChecker} that checks for a {@link Value.Raw} of the
   * given {@link Type}
   */
  export function getTypeCheckerFor<TType extends types.Type>(
    type: TType,
  ): TypeChecker<Raw<TType>>
  // NOTE: using overloads just to avoid type parameters in implementation
  // signature
  export function getTypeCheckerFor(type: types.Type): TypeChecker<Raw> {
    if (types.Enum.isEnum(type)) {
      // NOTE: this is not actually very effective for narrowing types, but it
      // does check for valid values, which is useful
      return TypeChecker.Factory.custom(
        type.hasEntry.bind(type) as <T>(val: T) => val is T,
        {
          typeDescription: `common.value.types.Enum: ${type.entries
            .map(entry => `${entry.name} (${entry.id})`)
            .join(', ')}`,
        },
      )
    }
    if (types.Structure.isStructure(type)) {
      return type.accepts
    }
    // NOTE: this is here because it has to be initialized after `Type`
    _typeCheckingMap ??= {
      [types.Intrinsic.Boolean]: TypeChecker.isBoolean,
      [types.Intrinsic.Currency]: TypeChecker.isNumber,
      [types.Intrinsic.Date]: TypeChecker.isDate,
      [types.Intrinsic.DateMultirange]: Multirange.getTypeChecker(
        TypeChecker.isDate,
      ),
      [types.Intrinsic.DateRange]: Range.getTypeChecker(TypeChecker.isDate),
      [types.Intrinsic.Decimal]: TypeChecker.isNumber,
      [types.Intrinsic.Integer]: TypeChecker.isNumber,
      [types.Intrinsic.List]: TypeChecker.isReadonlyArray,
      // NOTE: casting is needed because we use `Record<string, unknown>` instead
      // of `object`
      [types.Intrinsic.Object]: TypeChecker.isObject as TypeChecker<
        Raw<types.Intrinsic.Object>
      >,
      [types.Intrinsic.Percent]: TypeChecker.isNumber,
      [types.Intrinsic.Text]: TypeChecker.isString,
      [types.Intrinsic.Url]: TypeChecker.isString,
    }
    return _typeCheckingMap[type]
  }
  let _typeCheckingMap: {
    [TIntrinsic in types.Intrinsic]: TypeChecker<Raw<TIntrinsic>>
  }

  /**
   * Default {@link Value} for the given {@link Type}, if there is one
   */
  export function defaultFor<TType extends types.Type>(
    type: TType | undefined,
  ): Raw<TType> | undefined
  export function defaultFor(type: types.Type | undefined): Raw | undefined {
    if (!type) {
      return undefined
    }
    // TODO: add specific logic for other non-intrinsic types
    if (types.Enum.isEnum(type)) {
      return type.entries[0]?.id
    }
    const intrinsic = types.Intrinsic.from(type)
    if (types.Intrinsic.ForNumber.isForNumber(intrinsic)) {
      return 0
    }
    switch (intrinsic) {
      case types.Intrinsic.Text:
        return ''
      case types.Intrinsic.Boolean:
        return false
      case types.Intrinsic.List:
        return []
      case types.Intrinsic.Object:
        return {}
      case types.Intrinsic.DateRange:
        return new Range()
      case types.Intrinsic.DateMultirange:
        return new Multirange()
      default:
        return undefined
    }
  }

  // TODO: `cast` is complicated enough that it should live in its own file

  const _startSpecialCasters: SpecialCaster[] = []
  const _endSpecialCasters: SpecialCaster[] = []
  type SpecialCaster = (
    raw: unknown,
    type: types.Type,
  ) => ExactlyOneOf<{ return: Raw; continueWith: Raw }> | undefined

  // FIXME: This is the same type checker as Range.constructor.Params.isParams.
  // However, for some reason, the type checker from the Range namespace doesn't
  // work in this file. Try to investigate why the type checker from the Range
  // namespace isn’t functioning here.
  const isRangeParams: TypeChecker<Range.constructor.Params<any>> =
    TypeChecker.Factory.object({
      end: TypeChecker.isUnknown,
      includesEnd: TypeChecker.Factory.optional(TypeChecker.isBoolean),
      includesStart: TypeChecker.Factory.optional(TypeChecker.isBoolean),
      start: TypeChecker.isUnknown,
    })
  /**
   * Tries to force the given value to match the given type
   *
   * @example
   * common.value.Raw.cast('123', common.value.types.Intrinsic.Integer) === 123
   * common.value.cast(123, common.value.types.Intrinsic.Text) === '123'
   */
  export function cast<TType extends types.Type>(
    raw: unknown,
    type: TType,
  ): Raw<TType> | undefined
  // NOTE: this implementation signature exists so that we can avoid the type
  // parameters that would force us to use a lot of casting
  export function cast(raw: unknown, type: types.Type): Raw | undefined {
    // Uses special casters, if there are any that apply
    for (const startSpecialCaster of _startSpecialCasters) {
      const value = startSpecialCaster(raw, type)
      if (value?.return !== undefined) {
        // Only returns value if it is defined
        return value.return
      }
      if (value?.continueWith !== undefined) {
        raw = value.continueWith
      }
    }

    const castByType = (_raw: unknown): Raw | undefined => {
      if (TypeChecker.isNullish(_raw)) {
        // Null or undefined are both returned as undefined
        return undefined
      }

      if (type === types.Intrinsic.Text) {
        return _raw?.toString()
      } else if (type === types.Intrinsic.Boolean) {
        switch (typeof _raw) {
          case 'boolean':
            return _raw
          case 'number':
            return _raw !== 0
          case 'string':
            return LOWER_CASE_BOOLEAN_STRINGS[_raw.toLowerCase()]
          default:
            // If it is not any of the above types, we just use JavaScripts
            // built-in truthiness test
            return !!_raw
        }
      } else if (type === types.Intrinsic.Percent) {
        // If the value is a string that ends with a percent (ignoring trailing
        // spaces), then we need to divide the value by 100 (e.g. '90%' => 0.9)
        if (typeof _raw === 'string' && /%\s*/.test(_raw)) {
          const num = _toNumber(_raw)
          return num && num / 100
        }
        return _toNumber(_raw)
      } else if (type === types.Intrinsic.Integer) {
        const num = _toNumber(_raw)
        return num && Math.round(num)
      } else if (types.Intrinsic.ForNumber.isForNumber(type)) {
        return _toNumber(_raw)
      } else if (type === types.Intrinsic.Url) {
        // TODO: ensure URL is validly structured
        return _raw?.toString()
      } else if (type === types.Intrinsic.Date) {
        if (_raw instanceof Date) {
          // If it is already a date, we just return it
          return isNaN(_raw.valueOf()) ? undefined : _raw
        } else if (typeof _raw === 'string') {
          const date = new Date(_raw)
          // If the date is invalid, we return undefined
          return isNaN(date.valueOf()) ? undefined : date
        } else if (typeof _raw === 'number') {
          // NOTE: When importing data from Excel (.xlsx files), SheetJS emits
          // date cells as numeric serial numbers (days since Dec 30, 1899)
          // rather than JS Date objects, unless `cellDates: true` is passed to
          // xlsx.read(). We deliberately avoid that option because it forces
          // SheetJS to allocate a Date object for every cell in the already-
          // fully-loaded workbook, significantly increasing memory usage. To
          // handle this without touching the importer, we detect serials here
          // by range: valid Excel serials for 1900–2099 fall in [1, 2_958_466),
          // while Unix-epoch millisecond timestamps for the same calendar
          // period are ≥ ~946 × 10⁹. The two spaces never overlap, so the
          // heuristic is unambiguous.
          const date =
            _raw >= 1 && _raw < _EXCEL_MAX_SERIAL
              ? new Date(_EXCEL_EPOCH_MS + _raw * 86_400_000)
              : new Date(_raw)
          return isNaN(date.valueOf()) ? undefined : date
        }
      } else if (type === types.Intrinsic.DateRange) {
        const range =
          _raw instanceof Range
            ? _raw
            : _raw instanceof Multirange
              ? _raw.ranges[0]
              : undefined
        if (range) {
          // Even if we have a range, we still need to cast the endpoints
          return range.isEmpty
            ? Range.empty()
            : new Range({
                end: cast(range.end, types.Intrinsic.Date),
                includesEnd: range.includesEnd,
                includesStart: range.includesStart,
                start: cast(range.start, types.Intrinsic.Date),
              })
        } else if (isRangeParams(_raw)) {
          return new Range({
            end: cast(_raw.end, types.Intrinsic.Date),
            includesEnd: _raw.includesEnd,
            includesStart: _raw.includesStart,
            start: cast(_raw.start, types.Intrinsic.Date),
          })
        }
        if (typeof _raw === 'string') {
          // Assumes the string is in Postgres syntax
          // TODO: What happens if you get string that is not valid date?
          return Range.fromPostgres(_raw, val => new Date(val))
        } else {
          // If all else fails, we see if we can convert this into a date and then
          // make a range with just that date
          const date = cast(_raw, types.Intrinsic.Date)
          if (date instanceof Date) {
            return new Range({
              end: date,
              includesEnd: true,
              includesStart: true,
              start: date,
            })
          }
        }
      } else if (type === types.Intrinsic.DateMultirange) {
        const ranges =
          _raw instanceof Range
            ? [_raw]
            : _raw instanceof Multirange
              ? _raw.ranges
              : undefined
        if (ranges) {
          // We still need to convert the ranges to date ranges. Also, empty
          // ranges are implicitly handled in the Multirange constructor
          return new Multirange(
            ...ranges.map(range => cast(range, types.Intrinsic.DateRange)),
          )
        }
        if (typeof _raw === 'string') {
          // Assumes the string is in Postgres syntax
          // TODO: What happens if you get string that is not valid date?
          return Multirange.fromPostgres(_raw, val => new Date(val))
        } else if (Array.isArray(_raw)) {
          // If received value is an array, we try to convert it to Multirange by
          // checking if each array member is of type Range
          return new Multirange(
            _raw
              .map(range => cast(range, types.Intrinsic.DateRange))
              .filter(TypeChecker.isNotNullish.raw),
          )
        } else {
          // If all else fails, we see if we can convert this into a date range
          // and then make a multirange with just that date range
          const dateRange = cast(_raw, types.Intrinsic.DateRange)
          if (dateRange) {
            return new Multirange(dateRange)
          }
        }
      } else if (type === types.Intrinsic.List) {
        // If it is a non-list, we make it a list with the given value as one item
        return pack(_raw)
      } else if (type === types.Intrinsic.Object) {
        // In JavaScript, basically everything besides undefined and null, which
        // are handled above, is effectively an object
        return _raw as Object.Constraint
      } else if (types.Enum.isEnum(type)) {
        if (type.isMultiselect && Array.isArray(_raw)) {
          return _raw.flatMap(item => type.lookupEntry(item)?.id ?? [])
        }
        return type.lookupEntry(_raw)?.id
      } else if (types.Structure.isStructure(type)) {
        // NOTE: we treat this like an object with unknown properties even if it
        // is a list. In JavaScript a list can be used just like an object with
        // unknown properties
        const result = (
          type.intrinsic === types.Intrinsic.List ? [] : {}
        ) as Record<string, unknown>
        for (const [key, property] of Object.entries(type.properties.known)) {
          if (!property) {
            continue
          }
          result[key] = cast(
            (_raw as Unknown.ButNotNullish)[key],
            property.type,
          )
        }
        const otherPropertiesType = type.properties.others
        // If the structure supports unknown properties...
        if (otherPropertiesType) {
          for (const key in _raw as Unknown.ButNotNullish) {
            // And the raw type has an unknown property...
            if (!(key in type.properties.known)) {
              // Then we cast this property to the type specified for unknown
              // properties
              result[key] = cast(
                (_raw as Unknown.ButNotNullish)[key],
                otherPropertiesType,
              )
            }
          }
        }
        return result
      }

      if (_raw instanceof Range) {
        // If none of the above logic applies and we have a Range, we try to cast
        // its earliest defined endpoint
        const endpoint = _raw.start ?? _raw.end
        return cast(endpoint, type)
      }
      if (_raw instanceof Multirange) {
        // If none of the above logic applies and we have a Multirange, we try to
        // cast its first range
        const range = _raw.ranges[0]
        return cast(range, type)
      }

      // NOTE: if none of the above casting logic applies, we just have to hope
      // the value is already of the correct type
      return _raw as Raw
    }

    let casted = castByType(raw)
    for (const endSpecialCaster of _endSpecialCasters) {
      const value = endSpecialCaster(casted, type)
      if (value?.return !== undefined) {
        // Only returns value if it is defined
        return value.return
      }
      if (value?.continueWith !== undefined) {
        casted = value.continueWith
      }
    }

    return casted
  }
  export namespace cast {
    /**
     * Registers additional logic that will be used on every invocation of
     * {@link cast}
     *
     * Return `undefined` to fallback on default {@link cast} logic
     */
    export function registerSpecialHandler(
      when: 'start' | 'end',
      caster: SpecialCaster,
    ): void {
      if (when === 'start') {
        _startSpecialCasters.push(caster)
      } else {
        _endSpecialCasters.push(caster)
      }
    }
  }

  /**
   * Converts the given value to a number, taking into account various common
   * patterns (e.g. '$1,000')
   */
  function _toNumber(val: unknown): number | undefined {
    const num = Number(
      // Removes special characters if this is a string, since those mess with
      // the conversion
      typeof val === 'string' ? val.replaceAll(/[,$]/g, '') : val,
    )
    return isNaN(num) ? undefined : num
  }

  /**
   * Contains all strings that we accept for a boolean, and for each one,
   * indicates if we consider it `true` or `false`
   */
  const LOWER_CASE_BOOLEAN_STRINGS: Partial<Record<string, boolean>> = {
    /* eslint-disable @typescript-eslint/naming-convention */
    '0': false,
    '1': true,
    f: false,
    false: false,
    n: false,
    no: false,
    t: true,
    true: true,
    y: true,
    yes: true,
    /* eslint-enable @typescript-eslint/naming-convention */
  }
}
